import React from "react";

export interface AuditStep {
  id: string;
  label: string;
  /** Optional emoji or single character shown inside the node when upcoming. */
  icon?: string;
}

interface AuditProgressBarProps {
  steps: AuditStep[];
  /** The id of the current step. A non-matching value marks the journey complete. */
  currentStep: string;
  /** When true, suppresses labels below nodes for narrow contexts. */
  compact?: boolean;
  /** Enables completed steps as safe, keyboard-accessible return points. */
  onStepSelect?: (stepId: string) => void;
}

const COLOR_DONE = "#00C9A7";
const COLOR_ACTIVE = "#FF8C00";
const COLOR_FUTURE = "#2A3A4A";
const COLOR_LABEL_DONE = "#00C9A7";
const COLOR_LABEL_ACTIVE = "#FF8C00";
const COLOR_LABEL_FUTURE = "#556f85";
const COLOR_BG_DONE = "#00C9A7";
const COLOR_BG_ACTIVE = "#FF8C00";
const COLOR_BG_FUTURE = "#141C26";

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path
        d="M2.5 6.5l3 3 5-5"
        stroke="#0F1419"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Connector({ done }: { done: boolean }) {
  return (
    <div
      aria-hidden="true"
      style={{
        flex: 1,
        height: 2,
        marginBottom: 18,
        marginLeft: 2,
        marginRight: 2,
        background: done ? COLOR_DONE : COLOR_FUTURE,
        transition: "background 400ms ease-out",
        borderRadius: 2,
        boxShadow: done ? `0 0 6px ${COLOR_DONE}55` : "none",
      }}
    />
  );
}

interface StepNodeProps {
  step: AuditStep;
  index: number;
  isDone: boolean;
  isActive: boolean;
  compact: boolean;
  onSelect?: (stepId: string) => void;
}

function StepNode({ step, index, isDone, isActive, compact, onSelect }: StepNodeProps) {
  const bg = isDone ? COLOR_BG_DONE : isActive ? COLOR_BG_ACTIVE : COLOR_BG_FUTURE;
  const border = isDone ? COLOR_DONE : isActive ? COLOR_ACTIVE : COLOR_FUTURE;
  const labelColor = isDone ? COLOR_LABEL_DONE : isActive ? COLOR_LABEL_ACTIVE : COLOR_LABEL_FUTURE;
  const textColor = isDone || isActive ? "#0F1419" : "#556f85";
  const node = (
    <>
      <span
        aria-hidden="true"
        style={{
          width: 30,
          height: 30,
          flexShrink: 0,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: bg,
          border: `2px solid ${border}`,
          color: textColor,
          fontSize: "0.75rem",
          fontWeight: 900,
          transition: "background 300ms ease-out, border-color 300ms ease-out, box-shadow 300ms ease-out",
          boxShadow: isActive
            ? `0 0 0 4px ${COLOR_ACTIVE}22, 0 0 14px ${COLOR_ACTIVE}44`
            : isDone
              ? `0 0 8px ${COLOR_DONE}33`
              : "none",
        }}
      >
        {isDone ? <CheckIcon /> : (
          <span style={{ color: textColor, fontWeight: isActive ? 900 : 500, fontSize: "0.7rem" }}>
            {step.icon ?? String(index + 1)}
          </span>
        )}
      </span>
      {!compact && (
        <span
          style={{
            marginTop: 5,
            fontSize: "0.62rem",
            fontWeight: isActive ? 700 : 500,
            color: labelColor,
            textAlign: "center",
            whiteSpace: "nowrap",
            letterSpacing: isActive ? "0.03em" : "0",
            transition: "color 300ms ease-out",
          }}
        >
          {step.label}
        </span>
      )}
    </>
  );

  if (isDone && onSelect) {
    return (
      <button
        type="button"
        onClick={() => onSelect(step.id)}
        className="flex flex-col items-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#00C9A7]"
        aria-label={`Return to ${step.label}`}
        title={`Return to ${step.label}`}
        style={{ minWidth: 34, minHeight: 44, cursor: "pointer" }}
      >
        {node}
      </button>
    );
  }

  return (
    <div
      className="flex flex-col items-center"
      style={{ minWidth: 0 }}
      aria-current={isActive ? "step" : undefined}
      aria-label={isActive ? `${step.label}, current step` : `${step.label}, upcoming`}
    >
      {node}
    </div>
  );
}

/**
 * Shared visual progress control for the score journey. It reports true progress
 * to assistive technology and, where a parent supplies `onStepSelect`, lets a
 * visitor safely return only to previously completed form steps.
 */
export default function AuditProgressBar({
  steps,
  currentStep,
  compact = false,
  onStepSelect,
}: AuditProgressBarProps) {
  const currentIdx = steps.findIndex((step) => step.id === currentStep);
  const resolvedIdx = currentIdx === -1 ? steps.length : currentIdx;
  const currentStepNumber = Math.min(resolvedIdx + 1, steps.length);
  const activeLabel = currentIdx === -1
    ? "All assessment steps complete"
    : `${steps[currentIdx]?.label ?? "Assessment"}, step ${currentStepNumber} of ${steps.length}`;
  const canReturnToCompletedStep = Boolean(onStepSelect && resolvedIdx > 0 && currentIdx !== -1);

  return (
    <div>
      <div
        className="sr-only"
        role="progressbar"
        aria-label="Assessment progress"
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-valuenow={currentStepNumber}
        aria-valuetext={activeLabel}
      />
      {canReturnToCompletedStep && (
        <p className="mb-2 text-center text-xs" style={{ color: "#8899A6" }}>
          You can select a completed step to review it.
        </p>
      )}
      <nav
        aria-label="Assessment form progress"
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: compact ? "8px 0 4px" : "12px 0 4px",
        }}
      >
        {steps.map((step, index) => {
          const isDone = index < resolvedIdx;
          const isActive = index === resolvedIdx;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={step.id}>
              <StepNode
                step={step}
                index={index}
                isDone={isDone}
                isActive={isActive}
                compact={compact}
                onSelect={currentIdx !== -1 ? onStepSelect : undefined}
              />
              {!isLast && <Connector done={isDone} />}
            </React.Fragment>
          );
        })}
      </nav>
    </div>
  );
}
